import React from 'react';
import { View, Text, Modal, ScrollView, Pressable } from 'react-native';
import { X } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';

interface IRSInfoSheetProps {
  visible: boolean;
  onClose: () => void;
}

interface SectionProps {
  title: string;
  body: string;
  colors: any;
}

function Section({ title, body, colors }: SectionProps) {
  return (
    <View style={{ marginBottom: 18 }}>
      <Text
        style={{
          color: colors.text.primary,
          fontSize: 13,
          fontWeight: '700',
          marginBottom: 4,
          letterSpacing: -0.1,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          color: colors.text.secondary,
          fontSize: 12,
          lineHeight: 17,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

export function IRSInfoSheet({ visible, onClose }: IRSInfoSheetProps) {
  const colors = useThemeColors();

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: isWeb ? 'center' : 'flex-end',
          alignItems: 'center',
          padding: isWeb ? 20 : 0,
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: isWeb ? 520 : undefined,
            maxHeight: '85%',
            backgroundColor: colors.surface.primary,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderBottomLeftRadius: isWeb ? 20 : 0,
            borderBottomRightRadius: isWeb ? 20 : 0,
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
              paddingTop: 18,
              paddingBottom: 14,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            }}
          >
            <View>
              <Text
                style={{
                  color: colors.text.tertiary,
                  fontSize: 10,
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: 1.6,
                  marginBottom: 2,
                }}
              >
                Inverio Scorecard
              </Text>
              <Text
                style={{
                  color: colors.text.primary,
                  fontSize: 16,
                  fontWeight: '700',
                  letterSpacing: -0.2,
                }}
              >
                Methodology
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={12}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: colors.surface.secondary,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              accessibilityRole="button"
              accessibilityLabel="Close info"
            >
              <X size={16} color={colors.text.secondary} />
            </Pressable>
          </View>

          {/* Body */}
          <ScrollView
            contentContainerStyle={{ padding: 20, paddingBottom: 32 }}
            showsVerticalScrollIndicator={false}
          >
            <Section
              title="Overall Score"
              body="A composite 0-100 score combining venture quality (85%) and deal readiness (15%). Higher scores indicate stronger investability — ranging from Early Stage (under 40) to Highly Investable (80+). Underlying methodology: Investment Readiness Score (IRS)."
              colors={colors}
            />
            <Section
              title="Venture Quality"
              body="Weighted score across 15 dimensions: team, product, market, defensibility, traction, business model, GTM, financials, scalability, timing, vision, deal attractiveness, and social proof. Labeled Critical / Weak / Moderate / Strong / Excellent."
              colors={colors}
            />
            <Section
              title="Evidence Confidence"
              body="On the scorecard, Evidence is shown as an aggregated confidence score (0-100) reflecting how strong the supporting evidence is across all dimensions. Higher means more rigorous, lower means thinner. The flavor tagline (e.g. 'mostly verified', 'self-claim heavy') summarizes the source mix at a glance."
              colors={colors}
            />
            <Section
              title="Evidence Source (per dimension)"
              body="At the dimension level, evidence has two attributes. Source = where the claim comes from: None / Self-declared / 3rd Party / Verified. Confidence = how strong that evidence is, 0-100. A self-declared figure can have moderate confidence; a verified document can still have low confidence if stale or partial."
              colors={colors}
            />
            <Section
              title="Deal Readiness"
              body="Percentage of essential due diligence items present in the data room. Stage-aware checklist (pre-seed → Series B+) covering corporate, legal, IP, financial, and operational documents."
              colors={colors}
            />
            <Section
              title="Risks"
              body="Open issues flagged across dimensions, grouped by severity. High = blocking concerns requiring resolution. Medium = noteworthy items to address pre-close. Low = minor flags."
              colors={colors}
            />
            <Section
              title="Progression"
              body="Trend chip shows score change over the period (week or month). Sparkline visualizes the trajectory across recent scoring runs — momentum matters as much as the absolute number."
              colors={colors}
            />
            <Section
              title="Fundraising"
              body="Current round stage, target amount, committed (verified + soft commits), and progress percentage. Investors see this to gauge round timing and remaining allocation."
              colors={colors}
            />
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
